// Web Push Notification Utilities
import axios from 'axios';

// VAPID Public Key - Replace with your actual VAPID public key from backend
const VAPID_PUBLIC_KEY = 'BN5D54EY_MYnsQAbi_4W-7pwGc3zzOH6ztYKwl4StZSzzoh9W5j8DKMnNqMNFiQrc54ydM2UX3wXE9cGLUtdMBs';

interface PushSubscriptionData {
  endpoint: string;
  expirationTime: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

interface WebPushResponse {
  message: string;
}

class WebPushManager {
  private static instance: WebPushManager;
  private isSubscribed: boolean = false;
  private subscription: PushSubscription | null = null;

  private constructor() {}

  public static getInstance(): WebPushManager {
    if (!WebPushManager.instance) {
      WebPushManager.instance = new WebPushManager();
    }
    return WebPushManager.instance;
  }

  /**
   * Check if the browser supports service workers and push notifications
   */
  public isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Register the service worker
   */
  public async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!this.isSupported()) {
      console.warn('Web Push: Service Workers or Push Notifications are not supported.');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });

      console.log('Service Worker registered successfully');

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Request notification permission from the user
   */
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Notifications are not supported');
    }

    let permission = Notification.permission;

    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    return permission;
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Subscribe to push notifications
   */
  public async subscribeToPush(): Promise<PushSubscription | null> {
    try {
      const permission = await this.requestPermission();
      
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      const registration = await this.registerServiceWorker();
      if (!registration) {
        throw new Error('Service Worker registration failed');
      }

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        this.isSubscribed = true;
        return existingSubscription;
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      this.subscription = subscription;
      this.isSubscribed = true;

      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      throw error;
    }
  }

  /**
   * Send subscription data to backend
   */
  public async sendSubscriptionToBackend(subscription: PushSubscription): Promise<WebPushResponse> {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('User not authenticated');
    }

    // Extract subscription data
    const subscriptionData: PushSubscriptionData = {
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      keys: {
        p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
        auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
      }
    };

    try {
      const response = await axios.post<WebPushResponse>(
        'https://nrimarriage.in/api/v1/webpush/subscribe',
        subscriptionData,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Subscription sent to backend successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to send subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Convert ArrayBuffer to Base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Complete push notification setup
   */
  public async setupPushNotifications(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        console.warn('Push notifications are not supported');
        return false;
      }

      const subscription = await this.subscribeToPush();
      if (!subscription) {
        throw new Error('Failed to subscribe to push notifications');
      }

      await this.sendSubscriptionToBackend(subscription);
      
      return true;
    } catch (error) {
      console.error('Push notification setup failed:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  public async unsubscribe(): Promise<boolean> {
    try {
      if (!this.subscription) {
        console.log('No active subscription to unsubscribe from');
        return true;
      }

      const success = await this.subscription.unsubscribe();
      if (success) {
        this.subscription = null;
        this.isSubscribed = false;
      }

      return success;
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      return false;
    }
  }

  /**
   * Check current subscription status
   */
  public async getSubscriptionStatus(): Promise<{
    isSubscribed: boolean;
    permission: NotificationPermission;
    subscription: PushSubscription | null;
  }> {
    const permission = Notification.permission;
    
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        return {
          isSubscribed: !!subscription,
          permission,
          subscription
        };
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }

    return {
      isSubscribed: false,
      permission,
      subscription: null
    };
  }

  /**
   * Test notification (for development)
 */
public async testNotification(): Promise<void> {
  console.log('Attempting to send test notification...');
  console.log('Current Notification.permission:', Notification.permission); // Add this line

  if (Notification.permission === 'granted') {
    try { // Add try-catch block
      new Notification('DateNow Test', {
        body: 'This is a test notification from DateNow',
        icon: '/favicon.ico',
        tag: 'test-notification'
      });
      console.log('Test notification successfully created.'); // Add this line
    } catch (e) {
      console.error('Error creating Notification object:', e); // Add this line
    }
  } else {
    console.warn('Notification permission is not granted. Cannot show test notification.'); // Add this line
  }
}

  /**
   * Reset notification permission (for development/testing)
   * Note: This only works in some browsers and only during development
   */
  public async resetPermissionForTesting(): Promise<void> {
    // Try to unsubscribe first
    await this.unsubscribe();
    
    // Clear any stored subscription data
    this.subscription = null;
    this.isSubscribed = false;
  }

  /**
   * Get detailed permission and subscription info for debugging
   */
  public async getDebugInfo(): Promise<any> {
    const status = await this.getSubscriptionStatus();
    return {
      isSupported: this.isSupported(),
      permission: Notification.permission,
      isSubscribed: status.isSubscribed,
      hasSubscription: !!status.subscription,
      userAgent: navigator.userAgent,
      domain: window.location.hostname,
      protocol: window.location.protocol
    };
  }
}

// Export singleton instance
export const webPushManager = WebPushManager.getInstance();

// Export utility functions
export const {
  isSupported: isWebPushSupported,
  setupPushNotifications,
  unsubscribe: unsubscribeFromPush,
  getSubscriptionStatus,
  testNotification,
  resetPermissionForTesting,
  getDebugInfo
} = webPushManager;